/** 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 * One selection owner for a table and its tools; no record data or API access.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

type RowKey = string | number;
export interface TableSelectionLabels {
    toggle: string;
    selectedCount: string;
    clear: string;
    selectRow: string;
    selectAll: string;
}
const defaultLabels: TableSelectionLabels = {
    toggle: 'Select rows', selectedCount: '{count} selected', clear: 'Clear selection',
    selectRow: 'Select', selectAll: 'Select all rows on this page',
};
const TableSelectionContext = createContext<{
    active: boolean;
    keys: RowKey[];
    available: boolean;
    labels: TableSelectionLabels;
    toggle: () => void;
    clear: () => void;
    change: (keys: RowKey[]) => void;
    register: (keys: RowKey[]) => void;
} | null>(null);

/** Wrap exactly one table and its toolbar. A new displayed page clears selection. */
export function TableSelectionScope({ children, labels = defaultLabels }: { children: ReactNode; labels?: TableSelectionLabels }) {
    const [active, setActive] = useState(false);
    const [keys, setKeys] = useState<RowKey[]>([]);
    const [visibleKeys, setVisibleKeys] = useState<RowKey[]>([]);
    const clear = useCallback(() => setKeys([]), []);
    const toggle = useCallback(() => { setActive(value => !value); setKeys([]); }, []);
    const register = useCallback((next: RowKey[]) => { setVisibleKeys(next); setKeys([]); }, []);
    const change = useCallback((next: RowKey[]) => {
        const visible = new Set(visibleKeys);
        setKeys([...new Set(next)].filter(key => visible.has(key)));
    }, [visibleKeys]);
    const value = useMemo(() => ({ active, keys, available: visibleKeys.length > 0, labels, toggle, clear, change, register }),
        [active, keys, visibleKeys.length, labels, toggle, clear, change, register]);
    return <TableSelectionContext.Provider value={value}>{children}</TableSelectionContext.Provider>;
}

export const useTableSelectionScope = () => useContext(TableSelectionContext);
